import { useMemo } from "react"
import fp from "lodash/fp"
import { useDrawingArea, useXScale, useYScale } from "@mui/x-charts"
import { useTheme } from "@mui/material"

interface BackgroundRectangleProps {
  xStart: Date
  xEnd: Date
  yStart?: number
  yEnd?: number
  color?: string
  opacity?: number
}

const BackgroundRectangle = ({
  xStart,
  xEnd,
  yStart,
  yEnd,
  color,
  opacity = 0.1,
}: BackgroundRectangleProps) => {
  const theme = useTheme()
  const { height } = useDrawingArea()
  const xScale = useXScale()
  const yScale = useYScale()

  const startX = useMemo(() => xScale(xStart), [xScale, xStart])
  const endX = useMemo(() => xScale(xEnd), [xScale, xEnd])

  const rectWidth = useMemo(() => {
    if (fp.isNil(startX) || fp.isNil(endX)) return 0
    return endX - startX
  }, [startX, endX])

  const [yTop, rectHeight] = useMemo(() => {
    const hasCustomYRange = !fp.isNil(yStart) || !fp.isNil(yEnd)

    if (!yScale) {
      return [0, height]
    }

    const [yDomainMin] = yScale.domain()
    const xAxisPixelY = yScale(yDomainMin as number) as number

    if (hasCustomYRange) {
      // yEnd is higher data value = lower pixel Y (top of rect)
      // yStart is lower data value = higher pixel Y (bottom of rect)
      const topPixelY = !fp.isNil(yEnd) ? (yScale(yEnd) as number) : 0
      const bottomPixelY = !fp.isNil(yStart) ? (yScale(yStart) as number) : xAxisPixelY
      return [topPixelY, bottomPixelY - topPixelY]
    }

    return [0, xAxisPixelY]
  }, [yScale, height, yStart, yEnd])

  if (fp.isNil(startX) || fp.isNil(endX)) return null

  return (
    <rect
      x={startX}
      y={yTop}
      width={rectWidth}
      height={rectHeight}
      fill={color ?? theme.palette.neutral[500]}
      opacity={opacity}
    />
  )
}

export default BackgroundRectangle
